// Slice B2.4-C of docs/planning/activity_dock_workflow_facilitation.md:
// the dev-only consumer of the `ACTIVITY_DOCK_PEEK_EVENT` channel
// B2.4-B emits. Renders a live, session-scoped breakdown of dock
// peeks by `reasonSectionId`, mode, and assist level so a reviewer
// (platform_dev / platform_owner — already gated by the host DevPanel)
// can answer "which dock sections are pulling user attention, and
// which ones never fire?" without leaving the browser.
//
// L2-followup of docs/planning/firefly_role_context_presentation.md:
// the panel also exposes a "Peek readiness" probe that shows the
// LIVE decision-input state (per-section signal value + per-mode
// "would peek?" projection + assist-level gate). The probe was added
// to diagnose "I'm trying to fulfill the peek conditions but
// nothing is registering" — staring at the historical events panel
// while triggering nothing doesn't tell you WHY; the probe does. Still
// passive (no force-fire buttons — see doctrine below).
//
// What's intentionally NOT here:
//
//   - Persistence. State resets on page reload; live observation only.
//     Cross-session attribution is B2.4-D (`activity_dock_peek_telemetry`
//     + dev-panel cross-session rollup — platform_dev only).
//   - Action buttons that mutate the dock (e.g. "force a peek").
//     This panel is a passive observer; the dock's behavior is the
//     thing being measured. Adding test-fire affordances here would
//     pollute the stats with non-real peeks.
//   - Filtering / search. The view is small enough (≤ a handful of
//     sections, ≤ 13 modes, 4 assist levels) that aggregating once
//     and rendering it all is more useful than a controls strip.

import { useState } from "react";
import { useActivityDockPeekStats } from "@/components/activity/useActivityDockPeekStats";
import { DevPanelActivityDockPeekReadinessProbe } from "./DevPanelActivityDockPeekReadinessProbe";
import { DevPanelActivityDockPeekCrossSessionSection } from "./DevPanelActivityDockPeekCrossSessionSection";

const RECENT_PEEKS_DISPLAY_COUNT = 10;

function formatRelativeTs(ts: number): string {
  const deltaMs = Date.now() - ts;
  if (deltaMs < 0) return "just now";
  if (deltaMs < 1000) return "just now";
  if (deltaMs < 60_000) return `${Math.floor(deltaMs / 1000)}s ago`;
  if (deltaMs < 3_600_000) return `${Math.floor(deltaMs / 60_000)}m ago`;
  return `${Math.floor(deltaMs / 3_600_000)}h ago`;
}

export function DevPanelActivityDockPeekStatsSection() {
  const [collapsed, setCollapsed] = useState(true);
  const { state, summary, clear } = useActivityDockPeekStats();

  const recent = [...state.events]
    .slice(-RECENT_PEEKS_DISPLAY_COUNT)
    .reverse();

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/45">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
            Activity dock peek stats
          </h3>
          <p className="mt-1 text-xs text-gray-600 dark:text-zinc-300">
            Live session counter for{" "}
            <span className="font-mono">bb-activity-dock-peek</span>{" "}
            fires (B2.4-B). {summary.total} peek{summary.total === 1 ? "" : "s"} in this session
            {summary.lastTs !== null ? ` · last ${formatRelativeTs(summary.lastTs)}` : ""}.
          </p>
        </div>
        <span
          className="shrink-0 text-lg text-gray-500 dark:text-zinc-400"
          aria-hidden
        >
          {collapsed ? "\u25b8" : "\u25be"}
        </span>
      </button>

      {!collapsed ? (
        <div className="mt-3 space-y-3 border-t border-gray-200 pt-3 dark:border-zinc-700">
          <DevPanelActivityDockPeekReadinessProbe />
          <DevPanelActivityDockPeekCrossSessionSection enabled={!collapsed} />

          {summary.total === 0 ? (
            <p className="text-xs text-gray-600 dark:text-zinc-300">
              No peeks observed yet. Switch primary modes (e.g.{" "}
              <span className="font-mono">leads</span> →{" "}
              <span className="font-mono">manufacturing</span> → back to{" "}
              <span className="font-mono">leads</span>) on a sales account with
              unsent leads or an amber ads-coaching tone to trigger one.
            </p>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-3">
                <SummaryCard title="Top sections">
                  {summary.topReasons.length === 0 ? (
                    <EmptyHint />
                  ) : (
                    <ul className="space-y-1">
                      {summary.topReasons.map((row) => (
                        <li
                          key={row.reasonSectionId}
                          className="flex items-baseline justify-between gap-2 text-xs"
                        >
                          <span className="font-mono text-gray-800 dark:text-zinc-200">
                            {row.reasonSectionId}
                          </span>
                          <span className="tabular-nums text-gray-600 dark:text-zinc-400">
                            {row.count}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </SummaryCard>

                <SummaryCard title="By mode">
                  <CountList map={summary.perMode} />
                </SummaryCard>

                <SummaryCard title="By assist level">
                  <CountList map={summary.perAssistLevel} />
                </SummaryCard>
              </div>

              <SummaryCard title="(section, mode) hot spots">
                {summary.perReasonMode.size === 0 ? (
                  <EmptyHint />
                ) : (
                  <ul className="space-y-1">
                    {Array.from(summary.perReasonMode.entries()).map(
                      ([reason, byMode]) => (
                        <li key={reason} className="text-xs">
                          <span className="font-mono text-gray-800 dark:text-zinc-200">
                            {reason}
                          </span>
                          <span className="text-gray-500 dark:text-zinc-400">
                            {" → "}
                            {Array.from(byMode.entries())
                              .sort((a, b) => b[1] - a[1])
                              .map(([mode, count]) => `${mode} (${count})`)
                              .join(", ")}
                          </span>
                        </li>
                      ),
                    )}
                  </ul>
                )}
              </SummaryCard>

              <SummaryCard
                title={`Recent peeks (last ${Math.min(RECENT_PEEKS_DISPLAY_COUNT, summary.total)})`}
              >
                <ul className="space-y-1">
                  {recent.map((event, idx) => (
                    <li
                      key={`${event.ts}-${idx}`}
                      className="text-xs text-gray-700 dark:text-zinc-300"
                    >
                      <span className="tabular-nums text-gray-500">
                        {formatRelativeTs(event.ts)}
                      </span>
                      {" — "}
                      <span className="font-mono">{event.reasonSectionId}</span>
                      {" in "}
                      <span className="font-mono">{event.mode}</span>
                      {" · "}
                      <span className="text-gray-500">{event.assistLevel}</span>
                    </li>
                  ))}
                </ul>
              </SummaryCard>
            </>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="button"
              onClick={clear}
              disabled={summary.total === 0}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
            >
              Clear session
            </button>
            <p className="text-[11px] text-gray-500 dark:text-zinc-400">
              Live observation only — session log resets on reload. Cross-session
              rollup (B2.4-D) is in the panel above when expanded.
            </p>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function SummaryCard(props: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-2 dark:border-zinc-700 dark:bg-zinc-800/40">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:text-zinc-400">
        {props.title}
      </div>
      {props.children}
    </div>
  );
}

function CountList<K extends string>(props: {
  map: ReadonlyMap<K, number>;
}) {
  const entries = Array.from(props.map.entries()).sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return String(a[0]).localeCompare(String(b[0]));
  });
  if (entries.length === 0) return <EmptyHint />;
  return (
    <ul className="space-y-1">
      {entries.map(([key, count]) => (
        <li
          key={String(key)}
          className="flex items-baseline justify-between gap-2 text-xs"
        >
          <span className="font-mono text-gray-800 dark:text-zinc-200">
            {String(key)}
          </span>
          <span className="tabular-nums text-gray-600 dark:text-zinc-400">
            {count}
          </span>
        </li>
      ))}
    </ul>
  );
}

function EmptyHint() {
  return <p className="text-[11px] text-gray-500 dark:text-zinc-400">—</p>;
}
